<script setup>
import { ref, toRefs, useEventTracking, useSanity, watch } from '#imports';
import { getQuery as getSearch } from '~/groq-queries/search-themes';
import ArticlesList from '~/components/articles-list/articles-list.vue';
import SearchForm from '~/components/search-form/search-form.vue';

const sanity = useSanity();
const searchQuery = ref('');
const searchResults = ref([]);
const searchEmpty = ref(false);
const errorMessage = ref();
const searchPerformed = ref(false);

const props = defineProps({
  themeId: {
    type: String,
    default: '',
  },
});

const { themeId } = toRefs(props);

async function handlePerformSearch(value) {
  useEventTracking('Search', `User used search on theme page ${themeId.value}`);
  searchQuery.value = value;

  if (value.trim() === '') {
    return;
  }

  searchPerformed.value = true;

  try {
    const { query } = getSearch({
      searchQuery: searchQuery.value,
      themeId: themeId.value,
    });
    const result = await sanity.fetch(query);

    if (result.length === 0) {
      searchEmpty.value = true;
    } else {
      searchEmpty.value = false;
      searchResults.value = result;
    }
  } catch (error) {
    errorMessage.value = error.message;
  }
}

watch(searchQuery, (newValue) => {
  if (newValue === '') {
    searchEmpty.value = false;
    searchResults.value = [];
    searchPerformed.value = false;
  }
});
</script>

<template>
  <div class="articles-search">
    <div class="articles-search__form">
      <SearchForm
        v-model="searchQuery"
        with-button
        @search="handlePerformSearch"
        @reset="handlePerformSearch('')"
      />
    </div>

    <Transition name="fade" mode="out-in" type="transition">
      <div v-show="searchEmpty">
        <div class="articles-search__empty">
          <h2>
            По этому запросу ничего не найдено, но ниже будут наши последние
            материалы — они тоже супер!
          </h2>
        </div>

        <ArticlesList :theme-id="themeId" />
      </div>
    </Transition>

    <div v-show="searchQuery.value === '' || !searchPerformed">
      <ArticlesList :theme-id="themeId" />
    </div>

    <Transition name="fade" mode="out-in" type="transition">
      <div v-show="searchResults">
        <SearchResults
          :error-message="errorMessage"
          :search-empty="searchEmpty"
          :search-results="searchResults"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.articles-search {
  max-width: 960px;
  margin: 0 auto;

  & .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.1s;
  }

  & .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  &__form {
    margin-bottom: 16px;
  }

  &__empty {
    text-align: center;
    margin-top: 16px;
    margin-bottom: 48px;

    @media (max-width: 425px) {
      h2 {
        font-size: 16px;
        line-height: 22px;
      }
    }
  }

  &__content {
    max-width: 825px;
    margin: 0 auto;
    padding: 60px 0 160px 0;
  }

  &__title {
    font-size: var(--h1-font-size);
    line-height: var(--h1-line-height);
    font-weight: 700;
    margin-bottom: 24px;
  }

  &__subtitle {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-weight: 500;
    margin-bottom: 40px;
  }

  @media (--small-vp) {
    &__content {
      padding: 32px 0 60px 0;
    }

    &__title {
      font-size: 24px;
      line-height: 24px;
      margin-bottom: 16px;
    }

    &__subtitle {
      font-size: 16px;
      line-height: 22px;
      margin-bottom: 32px;
    }

    &__form {
      flex-direction: column;
    }
  }
}
</style>
